import { useLocation, useNavigate } from "react-router-dom";
import { CustomButton } from "../components/CustomButton";
import { Check, ClipboardList } from "lucide-react";
import { TimeSlot } from "../features/orders/types";
import { formatSlot } from "../utils/orderFormat";
import rushLogo from "../assets/png/rush-logo.png";

type OrderSuccessState = {
  id?: string;
  orderId?: string;
  pickupType?: "home" | "locker";
  pickupSlot?: TimeSlot | null;
  pickupSlotDate?: string | null;
};

export default function OrderSuccessScreen() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const orderSuccessState = (state as OrderSuccessState | null) ?? null;
  const orderId = orderSuccessState?.orderId;
  const isHome = orderSuccessState?.pickupType === "home";
  const slotText = formatSlot(orderSuccessState?.pickupSlot, orderSuccessState?.pickupSlotDate);

  return (
    <div className="min-h-[100dvh] md:min-h-0 bg-white px-8 md:py-14 flex flex-col items-center justify-center md:max-w-xl md:mx-auto md:rounded-3xl md:border md:border-gray-100 md:shadow-sm">
      
      <div className="w-full max-w-[320px] flex flex-col items-center">
        <img 
          src={rushLogo} 
          alt="Rush Logo" 
          className="h-6 w-auto object-contain mb-10" 
        />

        <div className="relative mb-6">
          <div className="absolute inset-0 bg-[#44B6CE]/10 rounded-full scale-125" />
          <div className="relative w-16 h-16 bg-[#44B6CE] rounded-full flex items-center justify-center shadow-lg shadow-cyan-100">
            <Check size={32} strokeWidth={3} className="text-white" />
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-xl font-black text-gray-900 tracking-tight">
            Order Confirmed!
          </h2>
          <p className="text-gray-500 text-[13px] font-medium mt-1.5 leading-relaxed">
            {isHome
              ? "Our courier will pick up your bag at the time you chose."
              : "Please drop your laundry bag in the locker."}
          </p>
        </div>

        <div className="w-full bg-gray-50 border border-gray-100 rounded-[22px] p-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2.5 rounded-xl shadow-sm border border-gray-100 flex-shrink-0">
              <ClipboardList className="text-[#44B6CE]" size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                Order Reference
              </p>
              <p className="text-sm font-black text-gray-800 truncate">
                #{orderId ?? "—"}
              </p>
            </div>
          </div>

          {isHome && slotText && (
            <p className="text-xs text-gray-600 font-semibold text-center mt-3">
              Pickup: {slotText}
            </p>
          )}
          
          <div className="w-full border-t border-dashed border-gray-200 my-3" />
          
          <p className="text-[10px] text-gray-400 font-medium text-center italic">
            "We'll list your items at the shop and send you the total to approve and pay."
          </p>
        </div>

        <CustomButton
          variant="primary"
          className="w-full h-12 text-sm rounded-xl font-bold shadow-md shadow-cyan-50 active:scale-[0.97] transition-all"
          onClick={() =>
            orderSuccessState?.id
              ? navigate(`/order/details?order=${orderSuccessState.id}`, { replace: true })
              : navigate("/home", { replace: true })
          }
        >
          View Order
        </CustomButton>
        <button
          className="mt-3 text-sm font-semibold text-gray-500"
          onClick={() => navigate("/home", { replace: true })}
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}
